import React from 'react';
import { useQueries, useQuery } from 'react-query';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';

import { listClassrooms, getCourseVersionDetails } from 'modules';
import { useProvider } from 'data/ProviderContext';
import { usePagination } from 'utils/usePagination';
import { useAcceptInvitations } from 'utils/useAcceptInvitations';
import ClassTable from './ClassTable';
import { columnDefinitions } from './columnDefinitions';
import { addCourseTitleToClassroomData } from './utils';

const useQueryOptions = {
    refetchOnWindowFocus: false,
};

const operation = 'listUserClassrooms';

const today = moment()
    .startOf('day')
    .toISOString();

const waitForConsistency = () => new Promise(res => setTimeout(res, 3000));

const MyClassesTable = () => {
    const { formatMessage } = useIntl();
    const providerArn = useProvider()?.arn;
    const acceptedInvitations = useAcceptInvitations();
    const { currentPage, addPage, goToPage, ...paginationProps } = usePagination();
    const {
        data,
        isLoading,
        refetch: refetchClassrooms,
        isSuccess: classroomsIsSuccess,
    } = useQuery(
        [operation, providerArn, today, currentPage],
        () =>
            providerArn
                ? listClassrooms({ providerArn, operation, after: today, nextToken: currentPage })
                : null,
        useQueryOptions
    );
    const { classrooms = [], courseIds = [], nextToken } = data || {};
    const courseQueries = useQueries(
        courseIds.map(courseId => ({
            queryKey: ['getCourseVersion', courseId],
            queryFn: () => getCourseVersionDetails(courseId, providerArn),
            ...useQueryOptions,
        }))
    );

    const supplementedClassroomData = addCourseTitleToClassroomData(
        classrooms,
        courseQueries.map(query => query.data)
    );

    React.useEffect(() => {
        if (nextToken) {
            addPage(nextToken);
        }
    }, [nextToken, addPage]);

    React.useEffect(() => {
        if (!acceptedInvitations || !acceptedInvitations.length) return;
        if (classroomsIsSuccess) {
            waitForConsistency().then(refetchClassrooms);
        }
    }, [acceptedInvitations, classroomsIsSuccess, refetchClassrooms]);

    return (
        <ClassTable
            isLoading={isLoading}
            data={supplementedClassroomData}
            columnDefinitions={columnDefinitions(formatMessage, false)}
            onPageChange={goToPage}
            {...paginationProps}
        />
    );
};

export default MyClassesTable;
